import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, theme } from 'antd';
import { Field } from '../../components/Field';
import { BodyMetricType, BodyMetricSourceType } from '@wellverse/shared-ts';
import type { BodyMetricLogDto } from '@wellverse/shared-ts';

const metricColors: Record<BodyMetricType, string> = {
  [BodyMetricType.WEIGHT]: '#1890ff',
  [BodyMetricType.HEIGHT]: '#722ed1',
  [BodyMetricType.BODY_FAT]: '#f5a623',
  [BodyMetricType.MUSCLE_MASS]: '#3ecf8e',
  [BodyMetricType.BONE_MASS]: '#13c2c2',
  [BodyMetricType.WATER_PERCENTAGE]: '#1677ff',
  [BodyMetricType.BMI]: '#eb2f96',
  [BodyMetricType.WAIST_CIRCUMFERENCE]: '#fa541c',
  [BodyMetricType.HIP_CIRCUMFERENCE]: '#a0d911',
  [BodyMetricType.CHEST_CIRCUMFERENCE]: '#2f54d1',
  [BodyMetricType.BICEP_CIRCUMFERENCE]: '#d9d9d9',
  [BodyMetricType.THIGH_CIRCUMFERENCE]: '#b37feb',
  [BodyMetricType.VISCERAL_FAT]: '#ff4d4f',
  [BodyMetricType.BMR]: '#fadb14',
  [BodyMetricType.BODY_AGE]: '#52c41a',
  [BodyMetricType.HEART_RATE]: '#ff6b6b',
  [BodyMetricType.BLOOD_PRESSURE_SYS]: '#13a8a8',
  [BodyMetricType.BLOOD_PRESSURE_DIA]: '#0e7090',
  [BodyMetricType.SYMPTOM]: '#9254de',
};

const sourceColors: Record<BodyMetricSourceType, string> = {
  [BodyMetricSourceType.MANUAL]: '#898989',
  [BodyMetricSourceType.APPLE_HEALTH]: '#52c41a',
  [BodyMetricSourceType.GOOGLE_FIT]: '#1890ff',
  [BodyMetricSourceType.GARMIN]: '#007cc3',
  [BodyMetricSourceType.FITBIT]: '#00b0b9',
  [BodyMetricSourceType.SAMSUNG_HEALTH]: '#1428a0',
  [BodyMetricSourceType.WITHINGS]: '#4bc9c0',
  [BodyMetricSourceType.XIAOMI]: '#ff6900',
  [BodyMetricSourceType.RENPHO]: '#7b2cbf',
  [BodyMetricSourceType.OPPO]: '#6b2737',
  [BodyMetricSourceType.HUAWEI_HEALTH]: '#cf222e',
  [BodyMetricSourceType.APPLE_WATCH]: '#000000',
  [BodyMetricSourceType.GARMIN_WATCH]: '#007cc3',
  [BodyMetricSourceType.POLAR]: '#6b4c9a',
  [BodyMetricSourceType.OTHER]: '#595959',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const BodyMetricLogShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<BodyMetricLogDto>({ resource: 'body-metric-admin/logs' });
  const item = queryResult?.data?.data;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  return (
    <Show resource="body-metric-admin/logs">
      <div style={{ padding: '8px 0' }}>
        <Field label="ID">{item.id}</Field>
        <Field label="User ID">{item.userId}</Field>
        {item.deviceSourceId && <Field label="Device Source ID">{item.deviceSourceId}</Field>}
        <Field label="Metric Type">
          <Tag style={tagStyle(metricColors[item.metricType] || '#898989')}>{item.metricType}</Tag>
        </Field>
        <Field label="Value">{`${item.value} ${item.unit}`}</Field>
        <Field label="Source Type">
          <Tag style={tagStyle(sourceColors[item.sourceType] || '#898989')}>{item.sourceType}</Tag>
        </Field>
        <Field label="Recorded At">{new Date(item.recordedAt).toLocaleString()}</Field>
        {item.notes && <Field label="Notes">{item.notes}</Field>}
        {item.metadata && (
          <Field label="Metadata">
            <pre style={{ background: token.colorFillAlter, padding: 8, borderRadius: 4, overflow: 'auto' }}>
              {JSON.stringify(item.metadata, null, 2)}
            </pre>
          </Field>
        )}
        <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
        <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
      </div>
    </Show>
  );
};